import { ANCHORS, type Anchor } from '../data/crew';

// Not imported from ./posts: that pulls in Firebase, which the unit tests cannot initialise.
const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');

/**
 * A calendar reminder for tomorrow, offered once, straight after the person
 * says when their moment is (owner, 2026-09-18). It is the plainest version of
 * an implementation intention: the plan, in their own words, at the time they
 * chose, in the place they already look. Nothing is sent anywhere — the file is
 * made on the device and handed to whatever calendar the phone uses.
 */
const TITLE = 'Kindling · make one small thing';

/** Tomorrow, at the hour the chosen moment implies (or the exact time they picked). */
export function reminderAt(anchor: Anchor | undefined, anchorTime: string | undefined, now = new Date()): Date {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  if (anchor === 'time' && anchorTime && /^\d{1,2}:\d{2}$/.test(anchorTime)) {
    const [h, m] = anchorTime.split(':').map(Number);
    d.setHours(h, m, 0, 0);
  } else {
    d.setHours(ANCHORS.find((a) => a.id === anchor)?.hour ?? 9, 0, 0, 0);
  }
  return d;
}

export function timeLabel(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const stampUTC = (d: Date) => `${d.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;

/** Wall-clock local time, the form /api/kindling/reminder.ics takes. */
export function icsStamp(at: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}${p(at.getMonth() + 1)}${p(at.getDate())}T${p(at.getHours())}${p(at.getMinutes())}00`;
}

/**
 * The link an iPhone will follow into Calendar.
 *
 * Not a blob and not a `download` attribute: Safari on iOS does nothing
 * legible with either, which is how a tester came to tap "Add the reminder",
 * get no Apple calendar sheet, and be told it had worked (interview
 * 2026-09-18). An ordinary https URL served as text/calendar is the thing iOS
 * knows what to do with, so the file itself is built by the backend, and its
 * shape is held by kindling-route.test.ts there.
 */
export function icsUrl(at: Date): string {
  return `${BACKEND}/api/kindling/reminder.ics?at=${icsStamp(at)}`;
}

/** The web fallback, for anyone whose browser will not open a downloaded .ics. */
export function googleCalendarUrl(at: Date): string {
  const end = new Date(at.getTime() + 15 * 60_000);
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: TITLE,
    dates: `${stampUTC(at)}/${stampUTC(end)}`,
    details: 'A few minutes. One exercise. Nobody sees it unless you send it.',
  });
  return `https://calendar.google.com/calendar/render?${p}`;
}
